// React Query hooks for the Interviewer Coach surface. The note and any in-flight message poll at
// 1.5s until they reach a terminal status; everything else is a one-shot fetch/mutation. There is
// no "apply" loop here — the coaching note is read-only (it never edits a guide).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CoachMessage,
  type CoachNote,
  getCoachMessage,
  getCoachNote,
  isTerminal,
  listCoachMessages,
  retryCoachMessage,
  sendCoachMessage,
} from './api';

const POLL_MS = 1500;

type InterviewId = number | string | undefined;

export function useCoachNote(interviewId: InterviewId, enabled = true) {
  return useQuery<CoachNote>({
    queryKey: ['coach-note', interviewId],
    queryFn: () => getCoachNote(interviewId!),
    enabled: enabled && Boolean(interviewId),
    retry: 1,
    // Poll while generating; stop once terminal OR the request errors (don't hammer a failing endpoint).
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return isTerminal(query.state.data?.status) ? false : POLL_MS;
    },
  });
}

export function useCoachMessages(interviewId: InterviewId, enabled = true) {
  return useQuery<CoachMessage[]>({
    queryKey: ['coach-messages', interviewId],
    queryFn: () => listCoachMessages(interviewId!),
    enabled: enabled && Boolean(interviewId),
  });
}

// Polls one message until it reaches a terminal status, surfacing its live progress label.
export function useCoachMessagePoll(
  interviewId: InterviewId,
  messageId?: number | null
) {
  return useQuery<CoachMessage>({
    queryKey: ['coach-message', interviewId, messageId],
    queryFn: () => getCoachMessage(interviewId!, messageId!),
    enabled: Boolean(interviewId) && Boolean(messageId),
    retry: 1,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false;
      return isTerminal(query.state.data?.status) ? false : POLL_MS;
    },
  });
}

export function useSendCoachMessage(interviewId: InterviewId) {
  const qc = useQueryClient();
  return useMutation<CoachMessage, unknown, string>({
    mutationFn: (query: string) => sendCoachMessage(interviewId!, query),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['coach-messages', interviewId] });
    },
  });
}

export function useRetryCoachMessage(interviewId: InterviewId) {
  return useMutation<CoachMessage, unknown, number>({
    mutationFn: (id: number) => retryCoachMessage(interviewId!, id),
  });
}
